import React, { useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { Formik } from 'formik';

import { getTourFeedbackTags } from 'common/tourHelpers';

import { TOUR_SHOWING_STATUS_COMPLETED, TOUR_TYPE_SELF_SHOWING } from 'common/tourConstants';

import {
  createInitialValues,
  createFormValidationFn,
  showFieldIf,
} from 'client/helpers/formikHelpers';

import { MEDIA_QUERY_SMALL_SCREEN } from 'client/constants/mediaQueries';

import useDispatch from 'client/hooks/useDispatch';

import TourActions from 'client/actions/Tour';
import SrAskWantsToApplyModal from 'client/components/SrAskWantstoApplyModal/SrAskWantsToApplyModal';
import SrTourFeedbackStepStars from './SrTourFeedbackStepStars';
import SrTourFeedbackStepFeedback from './SrTourFeedbackStepFeedback';

import './SrTourFeedback.less';

const getNextShowingToReview = ({ tours, tourId }) => {
  const tour = tours.find((tour) => tour.id === tourId);
  if (!tour) {
    return {};
  }

  const listing = tour.listings
    .filter((listing) => {
      return (
        listing.tourListingStatus === TOUR_SHOWING_STATUS_COMPLETED ||
        tour.type === TOUR_TYPE_SELF_SHOWING
      );
    })
    .find((listing) => {
      return !listing.tourListingReviewId && !listing.tourListingReviewSkipped;
    });

  return {
    tour,
    listing,
  };
};

function getFormFields({ values = {}, formikBag } = {}) {
  const shouldShowDirtyExplanationField = (values.tags || []).includes('Dirty');
  const shouldShowWtaExplanationField = values.rating === 5;
  const shouldShowFeedbackTitle = shouldShowDirtyExplanationField || shouldShowWtaExplanationField;

  return [
    {
      id: 'rating',
      type: 'star_rating',
      validations: ['positiveNumber'],
      properties: {
        onChange: (newValue) => {
          formikBag.setFieldValue('stepId', 1);
          if ((newValue === 5 && values.rating !== 5) || (newValue !== 5 && values.rating === 5)) {
            formikBag.setFieldValue('dirtyExplanation', '');
            formikBag.setFieldValue('wtaExplanation', '');
            formikBag.setFieldValue('tags', []);
          }
        },
      },
    },
    {
      id: 'tags',
      type: 'tags',
      properties: {
        choices: getTourFeedbackTags(values.rating).map((tag) => ({
          value: tag,
          label: tag,
        })),
      },
      validations:
        values.rating < 5 && !values.feedback ? ['nonEmptyArray::Please choose a reason'] : [],
    },
    showFieldIf(shouldShowDirtyExplanationField, {
      id: 'dirtyExplanation',
      title: 'What was dirty?',
      label: '(required)',
      type: 'long_text',
      properties: {
        placeholder: 'Please write as many details as possible.',
      },
      validations: ['required'],
    }),
    showFieldIf(shouldShowWtaExplanationField, {
      id: 'wtaExplanation',
      title: 'Do you want to apply?',
      type: 'long_text',
      properties: {
        placeholder: 'Write why or why not apply...',
      },
    }),
    {
      id: 'feedback',
      title: shouldShowFeedbackTitle ? 'General Feedback' : '',
      type: 'long_text',
      properties: {
        placeholder: 'Share your feedback',
      },
    },
    {
      id: 'stepId',
      type: 'hidden',
    },
  ];
}

const SrTourFeedback = ({ tours, tourId, isMobileApp }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSmallScreen = useMediaQuery({
    query: MEDIA_QUERY_SMALL_SCREEN,
  });
  const dispatch = useDispatch();

  const { tour, listing } = getNextShowingToReview({ tours, tourId });
  if (!listing) {
    return null;
  }

  const onClose = (formValues) => {
    if (!formValues.rating) {
      dispatch(TourActions.setShouldRequestRenterTourFeedback, false);
      return;
    }

    const tourFeedbackTags = getTourFeedbackTags(formValues.rating);

    dispatch(TourActions.rateShowing, {
      tourId,
      rating: formValues.rating,
      tags: formValues.tags.filter((tag) => tourFeedbackTags.includes(tag)),
      feedback: formValues.feedback,
      listing: listing,
      skipReview: true,
    });
  };

  const onSubmit = (formValues) => {
    const tourFeedbackTags = getTourFeedbackTags(formValues.rating);
    const tags = formValues.tags.filter((tag) => tourFeedbackTags.includes(tag));
    setIsSubmitting(true);

    dispatch(TourActions.rateShowing, {
      tourId,
      rating: formValues.rating,
      dirtyExplanation: (tags.includes('Dirty') && formValues.dirtyExplanation) || null,
      wtaExplanation: (formValues.rating === 5 && formValues.wtaExplanation) || null,
      tags,
      feedback: formValues.feedback,
      listing: listing,
    });
  };

  return (
    <Formik
      validate={(formValues) => {
        const formFields = getFormFields({ values: formValues });
        return createFormValidationFn(formFields)(formValues);
      }}
      initialValues={createInitialValues({
        formFields: getFormFields(),
        defaultValues: {
          rating: 0,
          stepId: 0,
        },
      })}
      onSubmit={onSubmit}
      render={(formikBag) => {
        const formFields = getFormFields({
          values: formikBag.values,
          formikBag,
        });

        return (
          <>
            {formikBag.values.stepId === 0 && (
              <SrTourFeedbackStepStars
                tour={tour}
                listing={listing}
                formikBag={formikBag}
                formFields={formFields}
                isSmallScreen={isSmallScreen}
                onClose={onClose}
              />
            )}
            {formikBag.values.stepId === 1 && (
              <SrTourFeedbackStepFeedback
                listing={listing}
                formikBag={formikBag}
                formFields={formFields}
                isSmallScreen={isSmallScreen}
                isSubmitting={isSubmitting}
                isMobileApp={isMobileApp}
                onClose={onClose}
              />
            )}
            {formikBag.values.stepId === 2 && (
              <SrAskWantsToApplyModal
                listing={listing}
                formikBag={formikBag}
                formFields={formFields}
                isSmallScreen={isSmallScreen}
                isSubmitting={isSubmitting}
              />
            )}
          </>
        );
      }}
    />
  );
};

SrTourFeedback.displayName = 'SrTourFeedback';

export default SrTourFeedback;
